import { useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'foundations-theming/utils';
import { rgba } from 'foundations-components/transitional/utils/color';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Tag } from 'foundations-components/transitional/components/Tag';
import { jsx as _jsx } from "react/jsx-runtime";
const MESSAGE_WIDTH = 280;
const MESSAGE_HEIGHT = 20;
const LEFT_MOUSE_BUTTON = 0;
const MIN_MOUSE_MOVEMENT = 10;
const dragAttemptedStyles = css(["background:repeating-linear-gradient( 145deg,transparent,transparent 30px,", " 30px,", " 33px );cursor:not-allowed;"], ({
  theme
}) => rgba(theme.color['fill-info-default'], 0.2), ({
  theme
}) => rgba(theme.color['fill-info-default'], 0.2));
const ClickOverlayWrapper = styled.div.withConfig({
  displayName: "ModuleNotDraggableLabel__ClickOverlayWrapper"
})([".hs-private.hs-private.hs-private &{display:flex;align-items:center;justify-content:center;position:absolute;z-index:5;", "}"], ({
  $dragAttempted
}) => $dragAttempted && dragAttemptedStyles);
const useWasDragAttempted = () => {
  const [dragAttempted, setDragAttempted] = useState(false);
  const isLeftMouseDown = useRef(false);
  const mousePositionOnLeftMouseDown = useRef(-1);
  const onMouseDown = useCallback(evt => {
    const {
      clientX,
      clientY
    } = evt;
    // While the click event assumes the left mouse button, mouse down can be any mouse button
    if (evt.button !== LEFT_MOUSE_BUTTON) return;
    mousePositionOnLeftMouseDown.current = clientX + clientY;
    isLeftMouseDown.current = true;
    setDragAttempted(false);
  }, []);
  const onClick = useCallback(evt => {
    // Defensively stop propagation of click and mouse up events if drag was attempted.
    if (dragAttempted) evt.stopPropagation();

    // Reset states here since click event follows the mouse up event
    setDragAttempted(false);
    mousePositionOnLeftMouseDown.current = -1;
    isLeftMouseDown.current = false;
  }, [dragAttempted]);
  const onMouseUp = useCallback(evt => {
    // Defensively stop propagation of click and mouse up events if drag was attempted.
    if (dragAttempted && evt.button === LEFT_MOUSE_BUTTON) evt.stopPropagation();
  }, [dragAttempted]);
  const onMouseMove = useCallback(evt => {
    const {
      clientX,
      clientY
    } = evt;

    // Abort if mouse is not down or if mouse has already moved since last mouse down
    if (!isLeftMouseDown.current || dragAttempted) {
      return;
    }
    const pixelsMouseMovedSinceMouseDown = Math.abs(clientX + clientY - mousePositionOnLeftMouseDown.current);

    // If mouse was moved enough since the last position of the mouse during mouse down
    if (pixelsMouseMovedSinceMouseDown > MIN_MOUSE_MOVEMENT) {
      setDragAttempted(true);
    }
  }, [dragAttempted]);
  return {
    dragAttempted,
    onClick,
    onMouseDown,
    onMouseUp,
    onMouseMove
  };
};
const ModuleNotDraggableLabel = ({
  height,
  width
}) => {
  const {
    dragAttempted,
    onClick,
    onMouseDown,
    onMouseUp,
    onMouseMove
  } = useWasDragAttempted();
  // Check if there is enough space to show the message with a conservative estimate
  const isEnoughSpaceToShowMessage = width > MESSAGE_WIDTH && height > MESSAGE_HEIGHT;
  return (
    /*#__PURE__*/
    // @bend-expect trellis-tokenized
    _jsx(ClickOverlayWrapper, {
      $dragAttempted: dragAttempted,
      onClick: onClick,
      onMouseDown: onMouseDown,
      onMouseMove: onMouseMove,
      onMouseUp: onMouseUp,
      style: {
        height,
        width
      },
      "data-test-id": "module-not-draggable-overlay",
      children: isEnoughSpaceToShowMessage && dragAttempted && /*#__PURE__*/_jsx(Tag, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "inpage.overlays.moduleNotDraggableMessage"
        })
      })
    })
  );
};
ModuleNotDraggableLabel.propTypes = {
  height: PropTypes.number.isRequired,
  width: PropTypes.number.isRequired
};
export default ModuleNotDraggableLabel;